import { useEffect, useMemo, useRef, useState } from "react";
import { projectAssetIds, type Project } from "@proof/core";
import { AssetImage } from "@proof/ui";
import { Select, Button, Control } from "./components";
import {
  bindAssetLibraryServices,
  snapshotAssetLibraryRecords,
  attachLibraryAsset,
  detachLibraryAsset,
  readLibraryAsset,
  downloadLibraryAsset,
  type AssetLibraryServices,
  type AssetLibraryRecord,
  type AssetLibraryArtifact,
} from "./asset-library";
const defaultDownload = (artifact: AssetLibraryArtifact) =>
  downloadLibraryAsset(artifact);

export interface AssetLibraryProps {
  tenantId: string;
  project?: Project;
  services: AssetLibraryServices;
  onChange?: (project: Project) => void;
  notify?: (message: string) => void;
  canApply?: boolean;
  canUpload?: boolean;
  initialScope?: "project" | "shared";
  onDownload?: (
    artifact: AssetLibraryArtifact,
    signal: AbortSignal,
  ) => void | Promise<void>;
}
export function AssetLibrary(props: AssetLibraryProps) {
  const {
    tenantId,
    project,
    canApply = true,
    canUpload = true,
    onDownload = defaultDownload,
  } = props;
  const validProject = project?.tenantId === tenantId ? project : undefined;
  const setup = useMemo(() => {
    try {
      if (
        typeof tenantId !== "string" ||
        !tenantId.trim() ||
        tenantId.length > 200 ||
        (project && project.tenantId !== tenantId)
      )
        throw Error();
      return { services: bindAssetLibraryServices(props.services) };
    } catch {
      return {
        error: "Provide an asset catalog and image loader for this workspace.",
      };
    }
  }, [
    props.services,
    tenantId,
    project?.id,
    project?.tenantId,
    canApply,
    canUpload,
    onDownload,
  ]);
  const latest = useRef({ setup, props });
  latest.current = { setup, props };
  const lifetime = useRef<AbortController | undefined>(undefined),
    operation = useRef<AbortController | undefined>(undefined);
  const [catalog, setCatalog] = useState<{
    setup: typeof setup;
    records: AssetLibraryRecord[];
  }>();
  const [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [scope, setScope] = useState<"project" | "shared">(
    validProject && props.initialScope !== "shared" ? "project" : "shared",
  );
  const [search, setSearch] = useState(""),
    [page, setPage] = useState(0);
  const records = catalog?.setup === setup ? catalog.records : [];
  const current = (signal: AbortSignal) =>
    latest.current.setup === setup && !signal.aborted;
  const announce = (message: string) => {
    setNotice(message);
    latest.current.props.notify?.(message);
  };
  const run = async (
    label: string,
    action: (signal: AbortSignal) => Promise<string | void>,
  ) => {
    if (!setup.services || !lifetime.current || lifetime.current.signal.aborted)
      return;
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    const signal = AbortSignal.any([
      lifetime.current.signal,
      controller.signal,
    ]);
    setBusy(label);
    setError("");
    setNotice("");
    try {
      const message = await action(signal);
      if (current(signal) && message) announce(message);
    } catch {
      if (current(signal))
        setError("Asset operation failed. Check the image services and retry.");
    } finally {
      if (current(signal) && operation.current === controller) setBusy("");
    }
  };
  const refresh = async (signal: AbortSignal) => {
    const raw = await setup.services!.listAssets(signal);
    if (!current(signal)) return;
    const next = snapshotAssetLibraryRecords(raw, tenantId);
    setCatalog({ setup, records: next });
    setPage(0);
  };
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    setCatalog(undefined);
    setBusy("");
    setError("");
    setNotice("");
    setSearch("");
    setPage(0);
    void run("Loading assets…", refresh);
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, [setup]);
  useEffect(() => {
    setScope(
      validProject && props.initialScope !== "shared" ? "project" : "shared",
    );
  }, [tenantId, project?.id, props.initialScope]);
  const attached = useMemo(
    () =>
      validProject ? projectAssetIds(validProject, records) : new Set<string>(),
    [validProject, records],
  );
  const used = useMemo(
    () =>
      validProject
        ? projectAssetIds(validProject, records, { includeLibrary: false })
        : new Set<string>(),
    [validProject, records],
  );
  const matches = records.filter(
    (row) =>
      (scope === "shared" ? row.shared !== false : attached.has(row.id)) &&
      `${row.name} ${row.id}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()),
  );
  const last = Math.max(0, Math.ceil(matches.length / 12) - 1),
    shown = Math.min(page, last);
  const load = useMemo(
    () =>
      setup.services
        ? async (id: string, signal: AbortSignal) => {
            const record = (
              catalog?.setup === setup ? catalog.records : []
            ).find((row) => row.id === id);
            if (!record) throw Error("Unknown image");
            const combined = lifetime.current
              ? AbortSignal.any([signal, lifetime.current.signal])
              : signal;
            return (await readLibraryAsset(record, setup.services!, combined))
              .blob;
          }
        : undefined,
    [setup, catalog],
  );
  return (
    <section
      className="panel padded managed-asset-library"
      aria-label="Managed asset library"
    >
      <div className="section-title">
        <h3>Image assets</h3>
        {canUpload && setup.services?.uploadAsset && (
          <label className="button">
            Upload asset
            <input
              className="sr-only"
              type="file"
              aria-label="Upload asset"
              accept=".png,.jpg,.jpeg,.webp,.tif,.tiff"
              disabled={
                !!busy ||
                (scope === "project" && (!canApply || !props.onChange))
              }
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                void run("Uploading image…", async (signal) => {
                  const result = await setup.services!.uploadAsset!(
                    file,
                    signal,
                    scope,
                  );
                  if (!current(signal)) return;
                  const record = snapshotAssetLibraryRecords(
                    [result],
                    tenantId,
                  )[0];
                  const raw = await setup.services!.listAssets(signal);
                  if (!current(signal)) return;
                  const next = snapshotAssetLibraryRecords(raw, tenantId);
                  const retained = next.find((row) => row.id === record.id);
                  if (!retained)
                    throw Error("Uploaded image is absent from the catalog");
                  setCatalog({ setup, records: next });
                  setPage(0);
                  if (scope === "project") {
                    setSearch(retained.name || retained.id);
                    const owner = latest.current.props;
                    if (
                      !owner.project ||
                      owner.project.tenantId !== tenantId ||
                      owner.project.id !== project?.id ||
                      owner.canApply === false ||
                      !owner.onChange
                    )
                      return;
                    owner.onChange(
                      attachLibraryAsset(owner.project, retained, next),
                    );
                  }
                  return `Uploaded ${record.name || "image"}.`;
                });
              }}
            />
          </label>
        )}
      </div>
      <p className="muted">
        PNG, JPEG, WebP or TIFF · up to 10 MB per upload. Project attachments
        reuse managed image bytes.
      </p>
      {setup.error && <p role="alert">{setup.error}</p>}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <div className="button-row">
        <Button
          disabled={!!busy || !setup.services}
          onClick={() => void run("Refreshing assets…", refresh)}
        >
          Refresh assets
        </Button>
        {busy && (
          <>
            <span role="status">{busy}</span>
            <Button
              onClick={() => {
                operation.current?.abort();
                setBusy("");
                announce("Asset operation cancelled.");
              }}
            >
              Cancel asset operation
            </Button>
          </>
        )}
      </div>
      <div className="control-grid">
        <Control label="Asset library scope">
          <Select
            value={scope}
            onChange={(event) => {
              operation.current?.abort();
              setBusy("");
              setScope(event.target.value as typeof scope);
              setPage(0);
            }}
          >
            <option value="project" disabled={!validProject}>
              This project{validProject ? ` · ${validProject.name}` : ""}
            </option>
            <option value="shared">Shared library</option>
          </Select>
        </Control>
        <Control label="Search assets">
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(0);
            }}
          />
        </Control>
      </div>
      <div className="button-row">
        <Button disabled={!shown} onClick={() => setPage(shown - 1)}>
          Previous assets
        </Button>
        <span>
          Page {shown + 1} of {last + 1} · {matches.length} images
        </span>
        <Button disabled={shown === last} onClick={() => setPage(shown + 1)}>
          Next assets
        </Button>
      </div>
      {!busy && catalog?.setup === setup && !matches.length && (
        <p role="status">
          No images match this view. Upload an image or choose Shared library.
        </p>
      )}
      <div className="asset-grid">
        {matches.slice(shown * 12, shown * 12 + 12).map((record) => (
          <article key={record.id} data-asset-id={record.id}>
            <AssetImage
              assetId={record.id}
              loadAsset={load}
              alt={record.name || "Managed image"}
            />
            <strong>{record.name || record.id}</strong>
            <small>
              {record.width} × {record.height} px ·{" "}
              {Math.ceil(record.bytes / 1024)} KB
            </small>
            <div className="button-row">
              <Button
                disabled={!!busy || !load}
                onClick={() =>
                  void run("Preparing image download…", async (signal) => {
                    const artifact = await readLibraryAsset(
                      record,
                      setup.services!,
                      signal,
                    );
                    if (!current(signal)) return;
                    await onDownload(artifact, signal);
                    signal.throwIfAborted();
                    return "Image download prepared.";
                  })
                }
              >
                Download image
              </Button>
              {validProject &&
                (scope === "shared" ? (
                  <Button
                    disabled={
                      !!busy ||
                      attached.has(record.id) ||
                      !canApply ||
                      !props.onChange
                    }
                    onClick={() => {
                      try {
                        props.onChange!(
                          attachLibraryAsset(validProject, record, records),
                        );
                        announce("Image added to this project.");
                      } catch {
                        setError("This image could not be attached.");
                      }
                    }}
                  >
                    {attached.has(record.id)
                      ? "In this project"
                      : "Add to this project"}
                  </Button>
                ) : (
                  <Button
                    disabled={
                      !!busy ||
                      used.has(record.id) ||
                      !canApply ||
                      !props.onChange
                    }
                    onClick={() => {
                      try {
                        props.onChange!(
                          detachLibraryAsset(validProject, record.id, records),
                        );
                        announce("Image removed from the project library.");
                      } catch {
                        setError("This image could not be detached.");
                      }
                    }}
                  >
                    Remove from project library
                  </Button>
                ))}
              {used.has(record.id) && <small>Used by this document</small>}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
